import {
  Box,
  Button,
  Modal,
  Toggle,
  InputField,
  Typography,
  useToast,
  getColor,
} from "fds";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { nanoid } from "@reduxjs/toolkit";
import { TypeDto } from "../../types/adminSetting";
import useAdminSettingStore from "../../redux/dispatcher/useAdminSettingStore";
import utilsCommon from "../../shared/utils/common";
import { LoginDto } from "../../types/auth";
import {
  getAdminInfoFromLocalStorage,
  getUserInfoFromLocalStorage,
} from "../../shared/utils/auth";

export type ModalProps = {
  showInsertOrUpdateType: boolean;
  targetType?: TypeDto;
  toggleInsertOrUpdateType: (open: boolean) => void;
};
const InsertOrUpdateTypeModal: React.FC<ModalProps> = ({
  showInsertOrUpdateType,
  targetType,
  toggleInsertOrUpdateType,
}) => {
  const user = getAdminInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  /** Hook 모음 */
  const { createManageType, updateManageType } = useAdminSettingStore();
  const { t } = useTranslation();

  /**** State 모음 ****/
  const [simpleInfo, setSimpleInfo] = useState({
    atName: "",
    atDesc: "",
  });
  const [allowAccessMenus, setAllowAccessMenus] = useState([] as string[]);
  const [isEditmode, setEditMode] = useState(false);

  const toast = useToast();
  /**** UseEffect 모음 ****/
  useEffect(() => {
    if (targetType && targetType?.atName) {
      setSimpleInfo({ atName: targetType?.atName, atDesc: targetType?.atDesc });
      setAllowAccessMenus(targetType.atMenuScope);
      setEditMode(true);
    }
  }, []);

  /*** Function 모음***/
  /** changeAllowAccessMenus: 관리자가 해당 메뉴를 선택/해제 가능하도록 하는 fucntion
   * cdnAvability 가 true일 경우, '스토리지 관리'메뉴도 선택/해제 가능하다.
   */
  const changeAllowAccessMenus = (menu: string) => {
    var tempMenuList = [...allowAccessMenus];
    if (menu === "all") {
      if (tempMenuList.length < 8 && utilsCommon.checkCdn(true)) {
        tempMenuList = [
          "eco_organ",
          "eco_manage_admin",
          "eco_position_role",
          "eco_workgroup",
          "eco_common_policy",
          "eco_storage",
          "eco_doc_usage",
          "eco_workgroup_log",
          "eco_login_log",
          "eco_trash",
        ];
      }
      if (tempMenuList.length < 7 && !utilsCommon.checkCdn(true)) {
        tempMenuList = [
          "eco_organ",
          "eco_manage_admin",
          "eco_position_role",
          "eco_workgroup",
          "eco_common_policy",
          "eco_doc_usage",
          "eco_workgroup_log",
          "eco_login_log",
          "eco_trash",
        ];
      } else {
        tempMenuList = [];
      }
    } else if (!tempMenuList.includes(menu)) {
      tempMenuList.push(menu);
    } else {
      tempMenuList.splice(tempMenuList.indexOf(menu), 1);
    }
    if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE") {
      tempMenuList = tempMenuList.filter(
        (item) => !item.includes("eco_workgroup")
      );
    }
    setAllowAccessMenus(tempMenuList);
  };
  const handleCreateOrUpdateType = async () => {
    if (isEditmode) {
      const resp: any = await updateManageType({
        atName: simpleInfo.atName,
        atDesc: simpleInfo.atDesc,
        atCode: targetType?.atCode,
        atMenuScope: allowAccessMenus,
      });
      if (resp.error) {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.modify"), "success");
      }
    } else {
      const resp: any = await createManageType({
        atName: simpleInfo.atName,
        atDesc: simpleInfo.atDesc,
        atMenuScope: allowAccessMenus,
      });
      if (resp.error) {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.add"), "success");
      }
    }

    toggleInsertOrUpdateType(false);
  };
  return (
    <Modal
      open={showInsertOrUpdateType}
      width={560}
      onClose={() => toggleInsertOrUpdateType(false)}
      title={t("admin.adminSetting.typeTitle")}
      subTitle={t("admin.adminSetting.typeDesc")}
      footer={
        <Box justifyContent="flex-end" classes="separateBox">
          <Button
            label={t("admin.common.button.cancel")}
            btnStyle="fill"
            type="secondary"
            onClick={() => toggleInsertOrUpdateType(false)}
          />
          <Button
            label={t("admin.common.button.ok")}
            btnStyle="fill"
            type="primary"
            state={simpleInfo.atName.length === 0 ? "disable" : "default"}
            onClick={() => handleCreateOrUpdateType()}
          />
        </Box>
      }
      body={
        <Box direction="column" width={"100%"}>
          <Typography type="subtitle2" classes={"typeModal-topBox"}>
            {t("admin.adminSetting.basicInfo")}
          </Typography>
          <Box classes="typeModal-box first">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.adminSetting.type")}
            </Typography>
            <InputField
              size={"md"}
              placeholder={t("admin.adminSetting.enterType")}
              classes="typeModal-text"
              value={simpleInfo.atName}
              onChange={(e) =>
                setSimpleInfo({ ...simpleInfo, atName: e.target.value })
              }
              maxLength={30}
            />
          </Box>
          <Box classes="typeModal-box last">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.adminSetting.desc")}
            </Typography>
            <InputField
              size={"md"}
              placeholder={t("admin.adminSetting.enterDesc")}
              classes="typeModal-text"
              value={simpleInfo.atDesc}
              onChange={(e) =>
                setSimpleInfo({ ...simpleInfo, atDesc: e.target.value })
              }
              maxLength={30}
            />
          </Box>
          <Box
            height={"60px"}
            alignItems={"flex-end"}
            classes={"typeModal-topBox"}
          >
            <Typography type="subtitle2">
              {t("admin.adminSetting.menu")}
            </Typography>
            <Button
              type="primary"
              btnStyle="link"
              label={t("admin.adminSetting.allMenu")}
              size="xsm"
              onClick={() => changeAllowAccessMenus("all")}
              textColor={getColor("COLOR_TEXT_BRAND")}
            />
          </Box>

          <Box classes="typeModal-box first">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.menu.dept")}
            </Typography>
            <Toggle
              label={
                allowAccessMenus.includes("eco_organ")
                  ? t("admin.common.button.allow")
                  : t("admin.common.button.block")
              }
              checked={allowAccessMenus.includes("eco_organ")}
              onChange={() => {
                changeAllowAccessMenus("eco_organ");
              }}
            />
          </Box>
          <Box classes="typeModal-box">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.menu.adminSetting")}
            </Typography>
            <Toggle
              label={
                allowAccessMenus.includes("eco_manage_admin")
                  ? t("admin.common.button.allow")
                  : t("admin.common.button.block")
              }
              checked={allowAccessMenus.includes("eco_manage_admin")}
              onChange={() => changeAllowAccessMenus("eco_manage_admin")}
            />
          </Box>
          <Box classes="typeModal-box">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.menu.position")}
            </Typography>
            <Toggle
              label={
                allowAccessMenus.includes("eco_position_role")
                  ? t("admin.common.button.allow")
                  : t("admin.common.button.block")
              }
              checked={allowAccessMenus.includes("eco_position_role")}
              onChange={() => changeAllowAccessMenus("eco_position_role")}
            />
          </Box>
          {loginDto?.ecoSystemLicenseStats?.featureAccessType !== "LITE" && (
            <Box classes="typeModal-box">
              <Typography
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("admin.menu.workgroup")}
              </Typography>
              <Toggle
                label={
                  allowAccessMenus.includes("eco_workgroup")
                    ? t("admin.common.button.allow")
                    : t("admin.common.button.block")
                }
                checked={allowAccessMenus.includes("eco_workgroup")}
                onChange={() => changeAllowAccessMenus("eco_workgroup")}
              />
            </Box>
          )}
          <Box classes="typeModal-box">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.menu.config")}
            </Typography>
            <Toggle
              label={
                allowAccessMenus.includes("eco_common_policy")
                  ? t("admin.common.button.allow")
                  : t("admin.common.button.block")
              }
              checked={allowAccessMenus.includes("eco_common_policy")}
              onChange={() => changeAllowAccessMenus("eco_common_policy")}
            />
          </Box>
          {utilsCommon.checkCdn(true) && (
            <Box classes="typeModal-box">
              <Typography
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("admin.menu.storage")}
              </Typography>
              <Toggle
                label={
                  allowAccessMenus.includes("eco_storage")
                    ? t("admin.common.button.allow")
                    : t("admin.common.button.block")
                }
                checked={allowAccessMenus.includes("eco_storage")}
                onChange={() => changeAllowAccessMenus("eco_storage")}
              />
            </Box>
          )}

          <Box classes="typeModal-box">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.menu.log")}
            </Typography>
            <Toggle
              label={
                allowAccessMenus.includes("eco_doc_usage")
                  ? t("admin.common.button.allow")
                  : t("admin.common.button.block")
              }
              checked={allowAccessMenus.includes("eco_doc_usage")}
              onChange={() => changeAllowAccessMenus("eco_doc_usage")}
            />
          </Box>
          {loginDto?.ecoSystemLicenseStats?.featureAccessType !== "LITE" && (
            <Box classes="typeModal-box">
              <Typography
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("admin.menu.workgroupLog")}
              </Typography>
              <Toggle
                label={
                  allowAccessMenus.includes("eco_workgroup_log")
                    ? t("admin.common.button.allow")
                    : t("admin.common.button.block")
                }
                checked={allowAccessMenus.includes("eco_workgroup_log")}
                onChange={() => changeAllowAccessMenus("eco_workgroup_log")}
              />
            </Box>
          )}
          <Box classes="typeModal-box">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.menu.loginLog")}
            </Typography>
            <Toggle
              label={
                allowAccessMenus.includes("eco_login_log")
                  ? t("admin.common.button.allow")
                  : t("admin.common.button.block")
              }
              checked={allowAccessMenus.includes("eco_login_log")}
              onChange={() => changeAllowAccessMenus("eco_login_log")}
            />
          </Box>
          <Box classes="typeModal-box last">
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("admin.menu.trash")}
            </Typography>
            <Toggle
              label={
                allowAccessMenus.includes("eco_trash")
                  ? t("admin.common.button.allow")
                  : t("admin.common.button.block")
              }
              checked={allowAccessMenus.includes("eco_trash")}
              onChange={() => changeAllowAccessMenus("eco_trash")}
            />
          </Box>
        </Box>
      }
    />
  );
};

export default InsertOrUpdateTypeModal;
